import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { SelectedSeat } from 'src/app/2dmap/types';
import { Viewer } from 'src/app/2dmap/viewer';
import Event from '../../models/event';
import RowTracker from '../../models/rowTracker';
import TicketType from '../../models/ticket.type';
import { TwoDMapService } from '../../services/2dmap.service';
import { UtilService } from '../../services/util.service';
import { Product } from '../../types/product.type';
import { BitmapChangeEventData } from '../threed-seats-picker/threed-seats-picker.component';
import { EventInfoComponent } from './components/event-info/event-info.component';
import { SeatSelectionSummaryMobileComponent } from './components/seat-selection-summary-mobile/seat-selection-summary-mobile.component';
import { SeatSelectionSummaryComponent } from './components/seat-selection-summary/seat-selection-summary.component';
import {
  BlockTicketType,
  TicketTypesLegendComponent,
} from './components/ticket-types-legend/ticket-types-legend.component';

@Component({
  selector: 'app-2map-seats-picker',
  standalone: true,
  imports: [
    CommonModule,
    EventInfoComponent,
    TicketTypesLegendComponent,
    SeatSelectionSummaryComponent,
    SeatSelectionSummaryMobileComponent,
  ],
  templateUrl: './2dmap-seats-picker.component.html',
  styleUrls: ['./2dmap-seats-picker.component.css'],
})
export class TwoDMapSeatsPickerComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Input() mapUrl = '';
  @Input() eventName = '';
  @Input() event!: Event;
  @Input() ticketType = '';
  @Input() ticketTypes: Array<BlockTicketType> = [];

  @Output() close = new EventEmitter<void>();
  @Output() confirmSeats = new EventEmitter<SelectedSeat[]>();
  @Output() public goToCheckout = new EventEmitter();
  @Output() public productEventEmitter = new EventEmitter<Product>();

  eventInfoExpanded = false;
  legendExpanded = false;
  selectedTicketsExpanded = true;

  products: Product[] = [];

  loading = false;

  private viewer!: Viewer;

  selectedSeats: SelectedSeat[] = [];

  constructor(
    private readonly twoDMapService: TwoDMapService,
    private readonly utilService: UtilService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.unmountSeatsTakenListener();
  }

  private async loadMap() {
    this.loading = true;
    if (!this.mapUrl) {
      console.warn('MAP URL WAS NOT FOUND.');
      return;
    }

    try {
      const state = await this.twoDMapService.getMap(this.mapUrl);

      const viewer = new Viewer({
        containerId: '2d-map-container',
        state,
        tixState: {
          ticketTypes: this.ticketTypes.map((tt) => ({
            mapId: tt.map_id!,
            name: tt.name,
            price: tt.price.toString(),
          })),
        },
      });

      viewer.show();
      this.viewer = viewer;
      this.blockOcuppiedSeats();
      this.onSelectedSeatsChange();

      this.ticketTypes = this.ticketTypes.map((tt) => {
        return {
          ...tt,
          map_id: tt.map_id!,
          color: this.viewer.getBlockColor(tt.map_id!)!,
          group: this.viewer.getBlockGroup(tt.map_id!)!,
        };
      });

      this.mountSeatsTakenListener();
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async openMap() {
    try {
      this.loading = true;
      this.isOpen = true;

      await this.loadMap();
    } catch (error: any) {
      console.error(error);

      const message =
        error?.message || JSON.stringify(error) || 'Error desconocido';

      alert(`Error cargando el mapa de asientos:\n${message}`);

      this.isOpen = false;
    } finally {
      this.loading = false;
    }
  }

  closeModalAndClearProducts() {
    this.clearProducts();
    this.closeModal();
  }

  closeModal() {
    this.isOpen = false;
  }

  clearProducts() {
    for (const p of this.products) {
      p.qty = 0;
      p.seats = [];
      this.productEventEmitter.emit(p);
    }
    this.selectedSeats = [];
  }

  onGoToCheckout() {
    const maxTicketsPerOrderErrors: { ticketType: TicketType; qty: number }[] =
      [];

    this.products.forEach((p) => {
      const tt = Object.values(this.ticketTypes)
        .flat()
        .find((t) => t.id === p.ticket_type_id);

      if (!tt) return;

      const minTicketsPerOrder = tt?.min_tickets_per_order || 1;

      if (p.qty !== 0 && p.qty < minTicketsPerOrder) {
        maxTicketsPerOrderErrors.push({ ticketType: tt, qty: p.qty });
      }
    });

    if (maxTicketsPerOrderErrors.length) {
      const message =
        'Cantidad mínima de tickets no cumplida:\n\n' +
        maxTicketsPerOrderErrors
          .map(
            (e) =>
              `• ${e.ticketType.name}: mínimo ${e.ticketType.min_tickets_per_order} (seleccionaste ${e.qty})`
          )
          .join('\n');

      alert(message);
      return;
    }

    this.confirmSeats.emit(this.selectedSeats);
    this.goToCheckout.emit();
    this.closeModal();
  }

  onOpen(component: 'legends' | 'eventInfo' | 'tickets') {
    if (component === 'eventInfo') {
      this.eventInfoExpanded = true;
      this.legendExpanded = false;
      this.selectedTicketsExpanded = false;
    } else if (component === 'tickets') {
      this.selectedTicketsExpanded = true;
      this.eventInfoExpanded = false;
      this.legendExpanded = false;
    } else {
      this.legendExpanded = true;
      this.selectedTicketsExpanded = false;
      this.eventInfoExpanded = false;
    }
  }

  onClose(component: 'legends' | 'eventInfo' | 'tickets') {
    if (component === 'eventInfo') {
      this.eventInfoExpanded = false;
    } else if (component === 'tickets') {
      this.selectedTicketsExpanded = false;
    } else {
      this.legendExpanded = false;
    }
  }

  private blockOcuppiedSeats(tt?: TicketType) {
    const blockTicketTypeSeats = (tt: TicketType) => {
      const seatsForBlock: { rowId: string; seatId: string }[] = [];
      tt.rows_trackers.forEach((rt) => {
        for (let i = 0; i < rt.str_rbitmap.length; i++) {
          if (rt.str_rbitmap[i] === '1' || rt.str_blocked_rbitmap[i] === '1') {
            seatsForBlock.push({
              seatId: (1 + i).toString(),
              rowId: rt.map_id || '',
            });
          }
        }
      });

      this.viewer.blockSeats(tt.map_id!, seatsForBlock);
    };

    if (!tt) {
      this.ticketTypes.forEach(blockTicketTypeSeats);
    } else {
      blockTicketTypeSeats(tt);
    }
  }

  private freeBlock(tt: TicketType, rt: RowTracker) {
    const ttRt = tt.rows_trackers.find((r) => r.id === rt.id);
    for (let i = 0; i < rt.str_rbitmap.length; i++) {
      const isNowFree =
        rt.str_rbitmap[i] === '0' && rt.str_blocked_rbitmap[i] === '0';
      const isPrevOccupied =
        ttRt?.str_rbitmap[i] === '1' || ttRt?.str_blocked_rbitmap[i] === '1';
      if (isNowFree && isPrevOccupied) {
        this.viewer.unblockSeat(tt.map_id!, {
          rowId: rt.map_id!,
          seatId: (i + 1).toString(),
        });
      }
    }
  }

  private removeOccupiedProductSeats(ttId: number, rt: RowTracker) {
    const p = this.products.find((p) => p.ticket_type_id === ttId);
    if (!p) return;

    const seatsToDelete: number[] = [];

    p.seats?.forEach((s, index) => {
      if (
        rt.str_rbitmap[s.seat_index - 1] === '1' ||
        rt.str_blocked_rbitmap[s.seat_index - 1] === '1'
      ) {
        p.qty--;
        const seatIndex = (seat: SelectedSeat) =>
          +seat.previewText.match(/\d+$/)![0];

        const indx = this.selectedSeats.findIndex(
          (se) => se.rowId === rt.map_id && seatIndex(se) === s.seat_index
        );
        this.selectedSeats.splice(indx, 1);
        seatsToDelete.push(index);
      }
    });

    seatsToDelete.forEach((index) => {
      p.seats?.splice(index, 1);
    });
  }

  private onSelectedSeatsChange() {
    this.viewer.onSelectedSeatsChange((seat, selected) => {
      const tt = this.ticketTypes.find((t) => t.map_id === seat.blockId);
      if (!tt) return;

      const seatIndex = seat.seatNumber;
      const rt = tt.rows_trackers.find((rt) => rt.map_id === seat.rowId)!;

      const maxTicketsPerOrder = tt.max_tickets_per_order;

      const totalTickets =
        (this.products.find((t) => t.ticket_type_id === tt.id)?.qty || 0) + 1;

      if (totalTickets > maxTicketsPerOrder && selected) {
        const message =
          'Cantidad máxima de tickets excedida:\n\n' +
          `• ${tt.name}: máximo ${maxTicketsPerOrder} por orden\n`;

        alert(message);

        this.viewer.unblockSeat(tt.map_id!, {
          rowId: rt.map_id!,
          seatId: seat.seatNumber.toString(),
        });
        return;
      }

      let p: Product | undefined = this.products.find(
        (p) => p.ticket_type_id === tt.id
      );

      if (!p) {
        p = {
          ticket_type_id: tt.id,
          seats: [],
          qty: 0,
        };

        this.products.push(p);
      }

      if (selected) {
        const productSeat = {
          tracker_id: rt.id,
          seat_index: seatIndex,
          preview_seat_index: +seat.previewText.match(/\d+$/)![0],
        };

        p.qty++;
        p.seats?.push(productSeat);
        seat.isTable = rt.prefix.toLowerCase().includes('mesa');
        this.selectedSeats.push(seat);
      } else {
        const indx = this.selectedSeats.findIndex(
          (s) => s.rowId === seat.rowId && s.seatId === seat.seatId
        );
        if (indx > -1) {
          this.selectedSeats.splice(indx, 1);
        }

        p.qty--;

        const seatProductIndex = p.seats?.findIndex(
          (s) => s.seat_index === seatIndex && s.tracker_id === rt.id
        )!;
        p.seats?.splice(seatProductIndex, 1);
      }

      this.productEventEmitter.emit(p);

      if (selected) {
        setTimeout(() => {
          const el = document.getElementById('selected-seat-' + seat.seatId);
          el?.scrollIntoView({
            behavior: 'smooth',
          });
        }, 0);
      }
    });
  }

  private mountSeatsTakenListener() {
    this.ticketTypes.forEach((tt) => {
      const key = this.getRowsNotificationBitmapEventListenerKey(tt.id);
      this.utilService.getSocket().on(key, (data: BitmapChangeEventData) => {
        const ttCpy = structuredClone(tt);
        const rt = tt.rows_trackers.find((rt) => rt.id === data.row_tracker_id);
        if (!rt) return;

        rt.str_rbitmap = data.str_rbitmap;
        rt.str_blocked_rbitmap = data.str_blocked_rbitmap;

        this.freeBlock(ttCpy, rt);
        this.removeOccupiedProductSeats(tt.id, rt);
        this.blockOcuppiedSeats(tt);
      });
    });
  }

  private unmountSeatsTakenListener() {
    this.ticketTypes.forEach((tt) => {
      const key = this.getRowsNotificationBitmapEventListenerKey(tt.id);
      this.utilService.getSocket().removeAllListeners(key);
    });
  }

  private getRowsNotificationBitmapEventListenerKey(ticketTypeId: number) {
    return `rows_${ticketTypeId}_notification_rbitmap`;
  }
}
